import { Input } from '@atlas/ui-primitives'
import { ExternalLink, X } from 'lucide-react'
import type { OriginalCode } from '@/domain/work-order.types'
import { useRowKeys } from '@/hooks/useRowKeys'

/** One original code as it is being edited. */
export interface OriginalCodeDraft {
	code: string
	description: string
	/** Kept so a saved link survives an edit of the description. */
	link: string | null
}

export const draftsFrom = (codes: OriginalCode[] = []): OriginalCodeDraft[] =>
	codes.map((code) => ({ code: code.code, description: code.description ?? '', link: code.link }))

/** The rows worth sending: blank codes dropped, text trimmed. */
export const cleanDrafts = (drafts: OriginalCodeDraft[]) =>
	drafts
		.filter((draft) => draft.code.trim())
		.map((draft) => ({
			code: draft.code.trim(),
			description: draft.description.trim() || null,
			link: draft.link,
		}))

/** The original codes as rows of code and description, to add, edit and remove. */
export function OriginalCodesEditor({
	value,
	onChange,
	error,
}: {
	value: OriginalCodeDraft[]
	onChange: (next: OriginalCodeDraft[]) => void
	error?: string
}) {
	const rowKeys = useRowKeys(value.length)
	const update = (index: number, patch: Partial<OriginalCodeDraft>) =>
		onChange(value.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)))
	const rows = value.map((draft, index) => ({ draft, key: rowKeys.keys[index] }))
	return (
		<div className="space-y-2">
			{rows.map(({ draft, key }, index) => (
				<div key={key} className="flex flex-wrap items-center gap-2">
					<Input
						aria-label={`Original code ${index + 1}`}
						value={draft.code}
						onChange={(e) => update(index, { code: e.target.value, link: null })}
						placeholder="ASSET-000000"
						className="w-40 font-mono"
					/>
					<Input
						aria-label={`Original code ${index + 1} description`}
						value={draft.description}
						onChange={(e) => update(index, { description: e.target.value })}
						placeholder="Description (optional)"
						className="min-w-48 flex-1"
					/>
					<button
						type="button"
						aria-label={`Remove original code ${index + 1}`}
						onClick={() => {
							rowKeys.remove(index)
							onChange(value.filter((_, i) => i !== index))
						}}
						className="text-ds-text-subtle hover:text-ds-danger-strong"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
			))}
			<button
				type="button"
				onClick={() => {
					rowKeys.add()
					onChange([...value, { code: '', description: '', link: null }])
				}}
				className="text-ds-sm font-medium text-ds-primary hover:underline"
			>
				+ Add original code
			</button>
			{error ? <p className="text-ds-sm text-ds-danger-strong">{error}</p> : null}
		</div>
	)
}

/** The original codes, read-only: each a link to its Vault document when it has one. */
export function OriginalCodesList({ codes }: { codes: Pick<OriginalCode, 'code' | 'description' | 'link'>[] }) {
	if (codes.length === 0) return <p className="text-sm text-ds-text-subtle">No original codes.</p>
	return (
		<ul className="space-y-1.5">
			{codes.map((code) => (
				<li key={code.code} className="text-sm">
					{code.link ? (
						<a
							href={code.link}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 font-mono font-medium text-ds-primary hover:underline"
						>
							{code.code}
							<ExternalLink className="h-3 w-3" aria-hidden />
						</a>
					) : (
						<span className="font-mono font-medium text-ds-text">{code.code}</span>
					)}
					{code.description ? <span className="ml-2 text-ds-text-muted">{code.description}</span> : null}
				</li>
			))}
		</ul>
	)
}
